import React, { memo } from 'react';
import DatePicker from 'react-datepicker';
import { fromJS } from 'immutable';
import moment from 'moment';

import SelectRange from '../helpers/SelectRange';
import { BrowseCheckbox } from './BrowseCheckbox';
import { BrowseControlContent } from './BrowseControlContent';
import { BrowseAutoComplete } from './BrowseAutoComplete';
import { BrowseDropDown } from './BrowseDropDown';
import { BrowseAutoCompleteTag } from './BrowseAutoCompleteTag';
import { Tag } from './Tag';
import { PresetDateRangeButtons } from './PresetDateRangeButtons';

const BrowseActivityFilterComponent = ({
  filter,
  buyersFilter,
  buyersInfo,
  info,
  projectTypeFilter,
  projectTypeInfo,
  usersFilter,
  usersInfo,
  statuses,
  onChange,
  onGetSuggestion,
  onGetActivityUserSuggestion,
  onGetNextActivityUserSuggestion,
  onSubActivityFilterChange,
  onGetBuyerSuggestion,
  onGetNextBuyerSuggestion,
}) => {
  const handleSelectPreset = (from, to) => {
    handleStartDateChange(moment(from.toDate()));
    handleEndDateChange(moment(to.toDate()));
  };

  const handleCheckboxChange = ({ filterName, filterData }) => {
    const shouldSetDefaultDates = filterData.checked && !info.get('startDate') && !info.get('endDate');

    onChange({
      filterName,
      filterData: shouldSetDefaultDates ? { ...filterData, startDate: moment(), endDate: moment() } : filterData,
    });
  };

  const handleChangeProjectType = data => {
    onChange({
      filterName: filter.get('name'),
      filterData: {
        [data.filterName]: data.filterData,
      },
    });
  };

  const handleValuesChange = ({ filterName, filterData }) => {
    onSubActivityFilterChange({
      filterName,
      filterData,
    });
  };

  const handleStartDateChange = date => {
    onChange({
      filterName: filter.get('name'),
      filterData: {
        startDate: date && date.isValid() ? date : null,
      },
    });
  };

  const handleEndDateChange = date => {
    onChange({
      filterName: filter.get('name'),
      filterData: {
        endDate: date && date.isValid() ? date : null,
      },
    });
  };

  const handleEventDescriptionChange = event => {
    const { value } = event.target;

    onChange({
      filterName: filter.get('name'),
      filterData: {
        eventDescription: value,
      },
    });
  };

  const handleAddTag = () => {
    const filterName = filter.get('name');
    const eventSelected = info.getIn(['selected', 'value']) || '(any activity)';
    const eventDescription = info.get('eventDescription') || '';

    const selected = fromJS({
      id: info.getIn(['selected', 'value']),
      text: eventDescription === '' ? eventSelected : `${eventSelected} - ${eventDescription}`,
      description: eventDescription,
    });
    const field = selected.has('id') ? 'id' : 'value';

    let selectedList = info.get('selectedList');

    if (selectedList.findIndex(s => s.get(field) === selected.get(field)) === -1) {
      selectedList = selectedList.push(selected.set('include', true));
    }

    onChange({
      filterName,
      filterData: {
        selectedList,
        text: '',
        selected: null,
        eventDescription: '',
      },
    });
  };

  const handleRemoveTag = event => {
    event.stopPropagation();

    const { name } = event.target;
    const tagIndex = parseInt(name.replace('tag', ''), 10);
    let selectedList = info.get('selectedList');

    selectedList = selectedList.delete(tagIndex);

    onChange({
      filterName: filter.get('name'),
      filterData: {
        selectedList,
      },
    });
  };

  const handleLastActivityChange = event => {
    const { checked } = event.target;

    // The options are mutually exclusive: checking one clears the other, unchecking leaves both off.
    onChange({
      filterName: filter.get('name'),
      filterData: checked ? { lastActivity: true, statusChanged: false } : { lastActivity: false },
    });
  };

  const handleStatusChangedChange = event => {
    const { checked } = event.target;

    onChange({
      filterName: filter.get('name'),
      filterData: checked ? { statusChanged: true, lastActivity: false } : { statusChanged: false },
    });
  };

  const handleChangeStatus = index => {
    const selectedList = info.get('selectedList').map((item, i) => {
      if (i === index) {
        return fromJS({
          ...item.toJS(),
          exclude: !item.get('exclude'),
        });
      }

      return fromJS(item);
    });

    onChange({
      filterName: filter.get('name'),
      filterData: {
        selectedList,
      },
    });
  };

  const hasActivityUser = usersFilter && usersInfo;

  const selectedList = info.get('selectedList');
  const statusChanged = info.get('statusChanged');

  const tagListContent =
    !statusChanged &&
    selectedList
      .filter(tag => tag)
      .map((tag, i) => (
        <Tag
          key={tag.has('id') ? `id_${tag.get('id')}` : `value_${tag.get('value')}`}
          exclude={tag.get('exclude')}
          index={i}
          onChangeStatus={handleChangeStatus}
          onRemoveTag={handleRemoveTag}
          value={tag.get('text')}
        />
      ));

  return (
    <BrowseCheckbox filter={filter} info={info} onChange={handleCheckboxChange}>
      <BrowseControlContent variant="activity">
        <div className="form-inline">
          <PresetDateRangeButtons onSelectPreset={handleSelectPreset} />
          <div className="form-group">
            <DatePicker
              className="form-control"
              dateFormat="MM/DD/YYYY"
              dropdownMode="select"
              onChange={handleStartDateChange}
              placeholderText="Start Date"
              selected={info.get('startDate')}
              showMonthDropdown
              showYearDropdown
            />
          </div>
          <div className="form-group">
            <DatePicker
              className="form-control"
              dateFormat="MM/DD/YYYY"
              dropdownMode="select"
              onChange={handleEndDateChange}
              placeholderText="End Date"
              selected={info.get('endDate')}
              showMonthDropdown
              showYearDropdown
            />
          </div>
          <div className="checkbox-wrap">
            <input checked={info.get('lastActivity')} id="last-activity-checkbox" onChange={handleLastActivityChange} type="checkbox" />
            <label htmlFor="last-activity-checkbox">Last Activity Only</label>
          </div>
          <div className="checkbox-wrap">
            <input checked={statusChanged} id="status-changed-checkbox" onChange={handleStatusChangedChange} type="checkbox" />
            <label htmlFor="status-changed-checkbox">Status Changed</label>
          </div>
          {info.get('statusChanged') ? (
            <div>
              <span>From</span>
              <SelectRange
                filter={filter.get('statusChangedFrom')}
                info={info.get('statusChangedFrom')}
                onChange={handleValuesChange}
                rangeData={statuses}
              />
              <span>To</span>
              <SelectRange
                filter={filter.get('statusChangedTo')}
                info={info.get('statusChangedTo')}
                onChange={handleValuesChange}
                rangeData={statuses}
              />
            </div>
          ) : null}
          {tagListContent}
          <BrowseAutoComplete
            className="form-control"
            filter={filter.get('eventActivity')}
            info={info}
            isHidden={statusChanged}
            onChange={onChange}
            onGetSuggestion={onGetSuggestion}
          />
          {!statusChanged && (
            <div className="form-group input-group">
              <input
                className="form-control"
                onChange={handleEventDescriptionChange}
                placeholder="Event Description"
                type="text"
                value={info.get('eventDescription')}
              />
              <span className="input-group-addon" onClick={handleAddTag}>
                <i aria-hidden="true" className="fa fa-plus" />
              </span>
            </div>
          )}
          {hasActivityUser && (
            <BrowseAutoCompleteTag
              filter={usersFilter}
              info={usersInfo}
              onChange={handleValuesChange}
              onGetNextSuggestion={onGetNextActivityUserSuggestion}
              onGetSuggestion={onGetActivityUserSuggestion}
              noCheckbox
            />
          )}
          <BrowseAutoCompleteTag
            filter={buyersFilter}
            info={buyersInfo}
            onChange={handleValuesChange}
            onGetNextSuggestion={onGetNextBuyerSuggestion}
            onGetSuggestion={onGetBuyerSuggestion}
            noCheckbox
          />
          {projectTypeFilter && projectTypeInfo && (
            <div className="form-group">
              <BrowseDropDown filter={projectTypeFilter} info={projectTypeInfo} onChange={handleChangeProjectType} />
            </div>
          )}
        </div>
      </BrowseControlContent>
    </BrowseCheckbox>
  );
};

/** Browse activity filter component. */
export const BrowseActivityFilter = memo(BrowseActivityFilterComponent);
